import { DateTime } from '@cheqroom/date-time';
import parseJwt from 'jwt-decode';

import { fetchUser, refreshToken } from '../../api/authentication';
import {
	AUTH_TOKEN_URL,
	AUTH_URL,
	DEFAULT_API_URL,
	MIN_REFRESH_TIME_IN_MINUTES,
	RESERVED_PATH_SEGMENTS,
	STORAGE_KEYS,
} from './constants';
import { mapUserApiResponseToUser } from './map-user-api-response';
import { resolveUserFromKnockout } from './resolve-user-knockout';
import type {
	AnonymousUser,
	AuthenticateResponse,
	AuthenticationOptions,
	JwtPayload,
	TokenPair,
	TokensByWorkspaceId,
	User,
} from './types';

export class Authentication {
	private tokenFetch: Promise<AuthenticateResponse> | null = null;
	private readonly options: Required<Pick<AuthenticationOptions, 'userSource'>> & {
		redirectToLogin?: () => void;
		apiBaseUrl: string;
	};
	private userCache: User | null = null;

	constructor(options: AuthenticationOptions = {}) {
		this.options = {
			redirectToLogin: options.redirectToLogin,
			userSource: options.userSource ?? 'knockout',
			apiBaseUrl: options.apiBaseUrl ?? DEFAULT_API_URL ?? '',
		};
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				this.storeLastActiveWorkspace(this.getWorkspaceId());
			}
		});
	}

	private isTokenAboutToExpire = (expiry: number, minRefreshTime: number): boolean => {
		const timeDifferenceMS = new Date(expiry * 1000).getTime() - new Date().getTime();
		const timeDifferenceMinutes = Math.round(timeDifferenceMS / 60000);
		return timeDifferenceMinutes < minRefreshTime;
	};

	private isTokenExpired = (expire: number): boolean => {
		return DateTime.now().isAfter(DateTime.fromDate(new Date(expire * 1000)));
	};

	private refetchToken = async (currentSessionToken: string): Promise<AuthenticateResponse> => {
		try {
			return await refreshToken(AUTH_TOKEN_URL, currentSessionToken);
		} catch {
			const tokens = this.getTokensForWorkspace();
			const data = {
				access_token: tokens.accessToken,
				refresh_token: tokens.sessionToken,
				token_type: 'Bearer',
			} as AuthenticateResponse;
			const accessTokenPayload = this.decode(data.access_token);
			if (!this.isTokenExpired(accessTokenPayload.exp)) return data;
			this.redirectToLogin();
			throw new Error('Could not fetch data');
		}
	};

	private toStorage = (accessToken: string, sessionToken: string) => {
		const tokensByWorkspaceId = this.fromStorage();
		const accessTokenPayload = this.decode(accessToken);
		tokensByWorkspaceId[accessTokenPayload.workspace] = {
			accessToken,
			sessionToken,
		};
		localStorage.setItem(STORAGE_KEYS.AUTH_TOKENS, JSON.stringify(tokensByWorkspaceId));
	};

	private fromStorage = (): TokensByWorkspaceId => {
		const rawTokens = localStorage.getItem(STORAGE_KEYS.AUTH_TOKENS);
		if (!rawTokens) {
			const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
			const refreshToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
			if (!accessToken || !refreshToken) return {};
			const accessTokenPayload = this.decode(accessToken);
			const tokens: TokensByWorkspaceId = {
				[accessTokenPayload.workspace]: { accessToken, sessionToken: refreshToken },
			};
			localStorage.setItem(STORAGE_KEYS.AUTH_TOKENS, JSON.stringify(tokens));
			localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
			localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
			return tokens;
		}
		try {
			return JSON.parse(rawTokens) as TokensByWorkspaceId;
		} catch {
			localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
			return {};
		}
	};

	authenticate = async () => {
		const currentLocation = new URL(window.location.toString());
		const hashParams = new URLSearchParams(currentLocation.hash.slice(1));
		const accessToken = hashParams.get('access_token') || currentLocation.searchParams.get('access_token');
		const sessionToken = hashParams.get('session_token') || currentLocation.searchParams.get('session_token');
		const redirectUri = currentLocation.searchParams.get('redirect_uri');

		currentLocation.hash = '';
		currentLocation.searchParams.delete('access_token');
		currentLocation.searchParams.delete('session_token');
		currentLocation.searchParams.delete('redirect_uri');

		if (redirectUri) {
			const [path, searchParams] = redirectUri.split('?');
			currentLocation.pathname = path;
			if (searchParams) currentLocation.search = searchParams;
		}

		window.history.replaceState({}, '', currentLocation);

		if (accessToken && sessionToken) {
			const decodedToken = this.decode(accessToken);
			this.storeLastActiveWorkspace(decodedToken.workspace);
			this.toStorage(accessToken, sessionToken);
			return [accessToken, sessionToken] as const;
		}
		this.storeLastActiveWorkspace(this.getWorkspaceId());
		return [await this.getAccessToken(), this.getSessionToken()] as const;
	};

	decode = (token: string): JwtPayload => {
		return parseJwt<JwtPayload>(token);
	};

	getAccessToken = async (): Promise<string> => {
		const tokens = this.getTokensForWorkspace();
		const accessToken = tokens.accessToken;
		const parsedToken = this.decode(accessToken);
		if (this.isTokenAboutToExpire(parsedToken.exp, MIN_REFRESH_TIME_IN_MINUTES)) {
			if (this.tokenFetch) return this.tokenFetch.then((res) => res.access_token);
			try {
				this.tokenFetch = this.refetchToken(this.getSessionToken());
				const { refresh_token: sessionToken, access_token: newAccessToken } = await this.tokenFetch;
				this.toStorage(newAccessToken, sessionToken);
				return newAccessToken;
			} finally {
				this.tokenFetch = null;
			}
		}
		return accessToken;
	};

	getSessionToken = (): string => {
		return this.getTokensForWorkspace().sessionToken;
	};

	logout = (): void => {
		localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVE_WORKSPACE);
		localStorage.removeItem(STORAGE_KEYS.AUTH_TOKENS);
		this.userCache = null;
		if (this.options.redirectToLogin) this.options.redirectToLogin();
	};

	redirectToLogin = (): void => {
		if (this.options.redirectToLogin) {
			this.options.redirectToLogin();
			return;
		}
		const workspaceId = this.getWorkspaceId();
		const hasWorkspaceIdInUrl = this.getWorkspaceIdFromPath();
		let redirectUrlWithoutWorkspaceId = window.location.pathname;
		if (hasWorkspaceIdInUrl) {
			redirectUrlWithoutWorkspaceId = `/${window.location.pathname.split('/').slice(2).join('/')}`;
		}
		if (window.location.search) {
			redirectUrlWithoutWorkspaceId = `${redirectUrlWithoutWorkspaceId}${window.location.search}`;
		}
		const path = workspaceId ? `/${workspaceId}/login` : '/workspaces';
		const loginUrl = new URL(`${AUTH_URL}${path}`);
		loginUrl.searchParams.set('redirect_uri', redirectUrlWithoutWorkspaceId);
		window.location.replace(loginUrl);
	};

	getUser({ anonymous }: { anonymous: true }): AnonymousUser;
	getUser({ anonymous }: { anonymous: false }): User;
	getUser(): User;
	getUser({ anonymous = false } = {}): User | AnonymousUser {
		const workspaceId = this.getWorkspaceId();
		let organisationId: string | undefined;
		try {
			const accessToken = this.getTokensForWorkspace()?.accessToken;
			if (accessToken) organisationId = this.decode(accessToken).organisation;
		} catch {
			// noop
		}

		if (anonymous) {
			return { organisationId, workspace: { id: workspaceId } };
		}

		if (this.options.userSource === 'api') {
			throw new Error(
				'getUser() is synchronous and only supported when userSource is "knockout". Use getUserAsync() when userSource is "api".'
			);
		}

		return resolveUserFromKnockout(organisationId ?? '');
	}

	getUserAsync = async (opts?: { anonymous?: boolean }): Promise<User | AnonymousUser | null> => {
		const anonymous = opts?.anonymous ?? false;
		const workspaceId = this.getWorkspaceId();
		let organisationId: string | undefined;
		try {
			const accessToken = this.getTokensForWorkspace()?.accessToken;
			if (accessToken) organisationId = this.decode(accessToken).organisation;
		} catch {
			// noop
		}

		if (anonymous) return { organisationId, workspace: { id: workspaceId } };

		if (this.options.userSource === 'knockout') {
			return this.getUser({ anonymous: false });
		}

		if (this.userCache) return this.userCache;

		if (!this.options.apiBaseUrl?.trim()) {
			if (process.env.NODE_ENV === 'development') {
				// eslint-disable-next-line no-console
				console.error('apiBaseUrl is required when userSource is "api"');
			}
			return null;
		}

		try {
			const accessToken = await this.getAccessToken();
			const payload = this.decode(accessToken);
			const apiUser = await fetchUser(payload.sub);
			const user = mapUserApiResponseToUser(apiUser);
			this.userCache = user;
			return user;
		} catch (error) {
			if (process.env.NODE_ENV === 'development') {
				// eslint-disable-next-line no-console
				console.error('Failed to get user:', error);
			}
			return null;
		}
	};

	private getTokensForWorkspace = (): TokenPair => {
		const workspaceId = this.getWorkspaceId();
		const tokensByWorkspaceId = this.fromStorage();
		const tokens = tokensByWorkspaceId[workspaceId];
		if (!tokens) throw new Error('No tokens for this workspace found');
		return tokens;
	};

	getWorkspaceId = (): string => {
		const fromPath = this.getWorkspaceIdFromPath();
		if (fromPath) return fromPath;
		const fromLastActive = this.getWorkspaceIdFromLastActive();
		if (fromLastActive) return fromLastActive;
		return this.getWorkspaceIdFromToken();
	};

	getWorkspaceIdFromPath = (): string | null => {
		const firstPathParameter = window.location.pathname.split('/')[1];
		if (firstPathParameter != null && RESERVED_PATH_SEGMENTS.includes(firstPathParameter)) return null;
		return firstPathParameter ?? null;
	};

	/** Stores tokens and last active workspace after login. Use after authenticate API returns. */
	storeTokens = (accessToken: string, sessionToken: string): void => {
		this.toStorage(accessToken, sessionToken);
		const payload = this.decode(accessToken);
		this.storeLastActiveWorkspace(payload.workspace);
	};

	private getWorkspaceIdFromLastActive = (): string | null => {
		return localStorage.getItem(STORAGE_KEYS.LAST_ACTIVE_WORKSPACE);
	};

	private getWorkspaceIdFromToken = (): string => {
		const tokensByWorkspaceId = this.fromStorage();
		const workspaceIds = Object.keys(tokensByWorkspaceId);
		return workspaceIds.length === 0 ? '' : workspaceIds[0];
	};

	private storeLastActiveWorkspace = (workspaceId: string) => {
		if (workspaceId) {
			localStorage.setItem(STORAGE_KEYS.LAST_ACTIVE_WORKSPACE, workspaceId);
		}
	};
}
